import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader, Card } from "../components/UI";

const DOCS = {
  hipaa: {
    title: "HIPAA Notice of Privacy Practices",
    sections: [
      ["Our Commitment", "Cocoa EHR is designed to help covered entities meet their obligations under the Health Insurance Portability and Accountability Act (HIPAA) and the HITECH Act. Protected Health Information (PHI) processed in this system is safeguarded through administrative, physical, and technical controls."],
      ["How PHI Is Protected", "Sensitive identifiers such as Social Security and Medicaid numbers are encrypted at rest. All access requires authentication, sessions expire after 15 minutes of inactivity, and optional two-factor authentication is available for every account. Every create, update, delete, view, and print action is written to an immutable audit trail."],
      ["Permitted Uses", "PHI in this system may be used for treatment, payment, and health care operations by authorized workforce members according to their assigned role and the clinic's permission matrix. Client portal accounts are strictly isolated to each client's own record."],
      ["Client Rights", "Clients may request access to their records, request amendments, and receive an accounting of disclosures. Direct such requests to your clinic's Privacy Officer."],
      ["Breach Notification", "Suspected privacy incidents should be reported to your organization's Privacy Officer immediately so statutory notification timelines can be met."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    sections: [
      ["Scope", "This policy describes how the Cocoa EHR platform handles information belonging to subscribing clinics (tenants), their workforce, and their clients."],
      ["Data We Process", "Account details (name, email, role), clinical records entered by the clinic, scheduling data, billing records, and technical logs required for security and reliability. Each tenant's data is logically isolated; no tenant can access another tenant's records."],
      ["How Data Is Used", "Solely to deliver the EHR service: charting, scheduling, task management, billing, reporting, and audit. We do not sell data, and we do not use client PHI for advertising."],
      ["Retention", "Clinical records are retained under the subscribing clinic's direction and applicable law. Audit logs are retained to support compliance reviews."],
      ["Your Choices", "Workforce users can manage notification preferences, session security, and personal display settings under Preferences and Security."],
    ],
  },
  terms: {
    title: "Terms of Use",
    sections: [
      ["Acceptance", "By signing in to Cocoa EHR you agree to these terms, your organization's policies, and all applicable healthcare regulations."],
      ["Authorized Use", "Access is provided for legitimate clinical, administrative, and billing duties only. Credential sharing is prohibited. Each user is responsible for actions taken under their account."],
      ["Prohibited Conduct", "Accessing records without a treatment, payment, or operations purpose; attempting to bypass role restrictions; exporting PHI outside approved workflows; and interfering with system availability are all prohibited and logged."],
      ["Service Availability", "The platform targets continuous availability. Planned maintenance is communicated in advance through system alerts."],
      ["Termination", "Your organization's administrator may suspend or remove accounts. Upon license expiry, access is limited according to your subscription plan."],
    ],
  },
  compliance: {
    title: "Compliance & Certifications",
    sections: [
      ["Regulatory Alignment", "Cocoa EHR aligns with HIPAA Security and Privacy Rules, 42 CFR Part 2 handling principles for substance use disorder records, and CARF documentation standards for behavioral health."],
      ["Technical Safeguards", "Field-level encryption for high-risk identifiers, TLS in transit, brute-force login protection, rate limiting, security headers, and per-tenant data isolation enforced at the data layer."],
      ["Clinical Governance", "Co-signature workflows for supervised clinicians, locked documents with addendum trails, treatment plan review timers, and golden-thread linkage between notes and plan goals."],
      ["Audit & Accountability", "A complete audit trail records who did what and when, with date-range and action filtering available to administrators under Reports."],
      ["Questions", "For compliance documentation requests, contact your clinic administrator or the support team listed in the Help Center."],
    ],
  },
  accessibility: {
    title: "Accessibility Statement",
    sections: [
      ["Our Goal", "Cocoa EHR aims to conform to WCAG 2.1 AA. We design for keyboard navigation, screen readers, and reduced-motion preferences."],
      ["Built-In Tools", "The floating accessibility widget provides larger text, high contrast, reduced motion, underlined links, and stronger focus outlines. Settings persist across sessions. A skip-to-content link is available on every page."],
      ["Feedback", "If you encounter an accessibility barrier, report it through the Help Center so we can address it promptly."],
    ],
  },
};

export default function LegalPage() {
  const { doc } = useParams();
  const d = DOCS[doc] || DOCS.privacy;
  useEffect(() => { document.title = `${d.title} | COCOA EHR`; window.scrollTo(0, 0); }, [d.title]);
  return (
    <div className="max-w-3xl" data-testid="legal-page">
      <PageHeader title={d.title} subtitle="Cocoa EHR legal & compliance library" />
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="legal-tabs">
        {Object.entries(DOCS).map(([k, v]) => (
          <Link key={k} to={`/legal/${k}`} data-testid={`legal-tab-${k}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${k === doc ? "bg-[#4E342E] text-white" : "bg-white border border-[#EFEBE0] text-[#5D4037] hover:bg-[#F5F2EB]"}`}>
            {v.title.split(" ").slice(0, 2).join(" ")}
          </Link>
        ))}
      </div>
      <Card className="p-7">
        {d.sections.map(([h, body], i) => (
          <div key={i} className={i > 0 ? "mt-5" : ""}>
            <h3 className="mb-1.5 font-heading text-sm font-bold text-[#3E2723]">{h}</h3>
            <p className="text-sm leading-relaxed text-[#5D4037]">{body}</p>
          </div>
        ))}
        <p className="mt-6 border-t border-[#F1EEE7] pt-4 text-xs text-[#84675B]">
          Last reviewed June 2026. This document is provided as a platform template; your organization's own policies govern where they are stricter.
        </p>
      </Card>
    </div>
  );
}
